import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  HistoryOutlined,
  RetweetOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  getInventoryTransferHistory,
  transferInventoryBetweenLocations,
} from "../../../Redux/actions/inventoryActions";
import { numberWithCommas } from "../../../utils/helpers";
import ImageChecker from "../../Generic/ImageChecker";
import ProductLogModal from "../../analytics/ProductLogModal";
import "./styles.scss";

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const REASON_OPTIONS = [
  { value: "rebalancing", label: "Rebalancing" },
  { value: "restock", label: "Restock" },
  { value: "correction", label: "Correction" },
  { value: "emergency", label: "Emergency" },
  { value: "return", label: "Return" },
];
const RECENT_STORAGE_KEY = "inventory-transfer-recent-pairs";
const FAVORITE_STORAGE_KEY = "inventory-transfer-favorite-pairs";
const RECENT_PAIR_LIMIT = 6;

const normalizeQty = (value) => {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return parsed;
};

const normalizeProduct = (item) => {
  if (!item) {
    return null;
  }

  return {
    id: item.id || item.key || item.product,
    key: item.id || item.key || item.product,
    name: item.name || item.product_name || "",
    product_name: item.name || item.product_name || "",
    sku: item.sku || "",
    unit: item.unit || "",
    transfer_quantity: normalizeQty(item.transfer_quantity),
    original: item,
  };
};

const mergeUniqueProducts = (currentProducts, incomingProducts) => {
  const mergedMap = new Map();

  (currentProducts || []).forEach((item) => {
    if (!item?.id) {
      return;
    }
    mergedMap.set(item.id, item);
  });

  (incomingProducts || []).forEach((item) => {
    const normalized = normalizeProduct(item);
    if (!normalized?.id) {
      return;
    }

    if (mergedMap.has(normalized.id)) {
      const existing = mergedMap.get(normalized.id);
      mergedMap.set(normalized.id, {
        ...normalized,
        transfer_quantity: existing.transfer_quantity,
      });
      return;
    }

    mergedMap.set(normalized.id, normalized);
  });

  return Array.from(mergedMap.values());
};

const getPairId = (sourceLocationId, destinationLocationId) =>
  `${sourceLocationId || ""}__${destinationLocationId || ""}`;

const readStoredPairs = (storageKey) => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const writeStoredPairs = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(value));
};

const formatStock = (value) => numberWithCommas(normalizeQty(value).toFixed(2));

const Index = ({
  visible,
  products,
  allProducts,
  locations,
  initialSourceLocationId,
  initialDestinationLocationId,
  onOpenProduct,
  onClose,
  onTransferred,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector(
    (state) => ({
      user: state.auth.user,
    }),
    shallowEqual
  );

  const [activeTab, setActiveTab] = useState("transfer");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sourceLocationId, setSourceLocationId] = useState(null);
  const [destinationLocationId, setDestinationLocationId] = useState(null);
  const [reason, setReason] = useState("rebalancing");
  const [note, setNote] = useState("");
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [productSearch, setProductSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [recentPairs, setRecentPairs] = useState([]);
  const [favoritePairs, setFavoritePairs] = useState([]);

  useEffect(() => {
    if (visible) {
      return;
    }
    setSelectedProducts([]);
    setActiveTab("transfer");
    setHistorySearch("");
    setHistoryItems([]);
    setSelectedCategories([]);
    setProductSearch("");
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setRecentPairs(readStoredPairs(RECENT_STORAGE_KEY));
    setFavoritePairs(readStoredPairs(FAVORITE_STORAGE_KEY));
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setSelectedProducts((currentProducts) =>
      mergeUniqueProducts(currentProducts, products || [])
    );
  }, [products, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (initialSourceLocationId) {
      setSourceLocationId((currentValue) => currentValue || initialSourceLocationId);
    }
    if (initialDestinationLocationId) {
      setDestinationLocationId((currentValue) =>
        currentValue || initialDestinationLocationId
      );
    }
  }, [initialDestinationLocationId, initialSourceLocationId, visible]);

  const sourceLocation = (locations || []).find(
    (location) => location.id === sourceLocationId
  );
  const destinationLocation = (locations || []).find(
    (location) => location.id === destinationLocationId
  );
  const currentPairId = getPairId(sourceLocationId, destinationLocationId);
  const currentPairIsFavorite = favoritePairs.some(
    (pair) => pair.id === currentPairId
  );
  const categoryOptions = Array.from(
    new Set(
      (allProducts || [])
        .map((item) => item.category_name)
        .filter(Boolean)
    )
  )
    .sort((a, b) => a.localeCompare(b))
    .map((categoryName) => ({
      label: categoryName,
      value: categoryName,
    }));

  const updateStoredPairLists = (pair) => {
    const normalizedPair = {
      id: getPairId(pair.sourceLocationId, pair.destinationLocationId),
      sourceLocationId: pair.sourceLocationId,
      sourceLocationName: pair.sourceLocationName,
      destinationLocationId: pair.destinationLocationId,
      destinationLocationName: pair.destinationLocationName,
      updatedAt: pair.updatedAt,
    };

    const nextRecentPairs = [
      normalizedPair,
      ...recentPairs.filter((item) => item.id !== normalizedPair.id),
    ].slice(0, RECENT_PAIR_LIMIT);

    setRecentPairs(nextRecentPairs);
    writeStoredPairs(RECENT_STORAGE_KEY, nextRecentPairs);
  };

  const toggleFavoritePair = () => {
    if (!sourceLocation || !destinationLocation) {
      message.warning("Choose both branches first.");
      return;
    }

    const pair = {
      id: currentPairId,
      sourceLocationId: sourceLocation.id,
      sourceLocationName: sourceLocation.name,
      destinationLocationId: destinationLocation.id,
      destinationLocationName: destinationLocation.name,
      updatedAt: new Date().toISOString(),
    };

    let nextFavorites = [];
    if (currentPairIsFavorite) {
      nextFavorites = favoritePairs.filter((item) => item.id !== pair.id);
    } else {
      nextFavorites = [pair, ...favoritePairs.filter((item) => item.id !== pair.id)];
    }

    setFavoritePairs(nextFavorites);
    writeStoredPairs(FAVORITE_STORAGE_KEY, nextFavorites);
  };

  const applyPair = (pair) => {
    setSourceLocationId(pair.sourceLocationId);
    setDestinationLocationId(pair.destinationLocationId);
  };

  const updateProductQuantity = (productId, value) => {
    setSelectedProducts((currentProducts) =>
      currentProducts.map((item) =>
        item.id === productId
          ? { ...item, transfer_quantity: normalizeQty(value) }
          : item
      )
    );
  };

  const setProductTransferQuantity = (productRecord, value) => {
    const normalizedValue = normalizeQty(value);

    setSelectedProducts((currentProducts) => {
      const existing = currentProducts.find((item) => item.id === productRecord.id);

      if (!existing && normalizedValue <= 0) {
        return currentProducts;
      }

      if (existing) {
        if (normalizedValue <= 0) {
          return currentProducts.filter((item) => item.id !== productRecord.id);
        }

        return currentProducts.map((item) =>
          item.id === productRecord.id
            ? { ...item, transfer_quantity: normalizedValue }
            : item
        );
      }

      return currentProducts.concat({
        ...normalizeProduct(productRecord),
        transfer_quantity: normalizedValue,
      });
    });
  };

  const removeSelectedProduct = (productId) => {
    setSelectedProducts((currentProducts) =>
      currentProducts.filter((item) => item.id !== productId)
    );
  };

  const swapBranches = () => {
    setSourceLocationId(destinationLocationId);
    setDestinationLocationId(sourceLocationId);
  };

  const getStockForProduct = (product, locationId) => {
    const fullProduct =
      (allProducts || []).find((item) => item.id === product.id) ||
      product.original;

    if (!fullProduct || !locationId) {
      return 0;
    }

    return normalizeQty(fullProduct[`${locationId}_count`]);
  };

  const normalizedSearchText = (productSearch || "").trim().toLowerCase();
  const productCatalog = (allProducts || [])
    .map((item) => {
      const normalizedProduct = normalizeProduct(item);
      const sourceStock = getStockForProduct(normalizedProduct, sourceLocationId);
      const destinationStock = getStockForProduct(
        normalizedProduct,
        destinationLocationId
      );
      const selectedProduct = selectedProducts.find(
        (selectedItem) => selectedItem.id === normalizedProduct.id
      );

      return {
        ...normalizedProduct,
        sourceStock,
        destinationStock,
        transfer_quantity: normalizeQty(selectedProduct?.transfer_quantity),
      };
    })
    .filter((item) => {
      if (showOnlyAvailable && sourceLocationId && item.sourceStock <= 0) {
        return false;
      }

      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(item.original?.category_name)
      ) {
        return false;
      }

      if (!normalizedSearchText) {
        return true;
      }

      const searchableText = `${item.name} ${item.sku || ""} ${item.id}`.toLowerCase();
      return searchableText.includes(normalizedSearchText);
    });

  const productTableColumns = [
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (text, record) => (
        <div className="inventory-transfer-table-product">
          <div className="inventory-transfer-table-product-main">
            <button
              type="button"
              className="inventory-transfer-image-trigger"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (typeof onOpenProduct === "function") {
                  onOpenProduct(record.original || record);
                }
              }}
            >
              <ImageChecker
                url={`${process.env.REACT_APP_AWS_S3}${record.id}.png`}
                success={
                  <img
                    src={`${process.env.REACT_APP_AWS_S3}${record.id}.png`}
                    alt="product"
                    className="inventory-transfer-thumb"
                  />
                }
                fail={
                  <img
                    src={`${process.env.REACT_APP_AWS_S3}logo192.png`}
                    alt="product"
                    className="inventory-transfer-thumb"
                  />
                }
              />
            </button>
            <div className="inventory-transfer-table-product-copy">
              <Text strong>{text}</Text>
              {record.sku ? <Text type="secondary">{record.sku}</Text> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: sourceLocation ? `${sourceLocation.name} Qty` : "Source Qty",
      dataIndex: "sourceStock",
      key: "sourceStock",
      width: 140,
      render: (value, record) => {
        const displayValue = `${formatStock(value)} ${record.unit || ""}`;

        if (!sourceLocation) {
          return displayValue;
        }

        return (
          <ProductLogModal
            display={displayValue}
            branch={sourceLocation}
            thisItem={record.original || record}
          />
        );
      },
    },
    {
      title: destinationLocation
        ? `${destinationLocation.name} Qty`
        : "Destination Qty",
      dataIndex: "destinationStock",
      key: "destinationStock",
      width: 160,
      render: (value, record) => {
        const displayValue = `${formatStock(value)} ${record.unit || ""}`;

        if (!destinationLocation) {
          return displayValue;
        }

        return (
          <ProductLogModal
            display={displayValue}
            branch={destinationLocation}
            thisItem={record.original || record}
          />
        );
      },
    },
    {
      title: "Transfer Qty",
      dataIndex: "transfer_quantity",
      key: "transfer_quantity",
      width: 190,
      render: (value, record) => (
        <Space className="inventory-transfer-table-actions">
          <InputNumber
            min={0}
            value={value}
            onChange={(nextValue) => {
              setProductTransferQuantity(record, nextValue);
            }}
          />
          <Button
            size="small"
            onClick={() => {
              setProductTransferQuantity(record, record.sourceStock);
            }}
            disabled={record.sourceStock <= 0}
          >
            Max
          </Button>
        </Space>
      ),
    },
  ];

  const buildTransferPayload = () =>
    selectedProducts
      .map((item) => {
        const sourceStock = getStockForProduct(item, sourceLocationId);
        const destinationStock = getStockForProduct(item, destinationLocationId);

        return {
          id: item.id,
          name: item.name,
          sku: item.sku,
          unit: item.unit,
          transfer_quantity: normalizeQty(item.transfer_quantity),
          sourceStock,
          destinationStock,
        };
      })
      .filter((item) => item.transfer_quantity > 0);

  const transferPreviewProducts = buildTransferPayload();
  const totalTransferQuantity = transferPreviewProducts.reduce(
    (sum, item) => sum + item.transfer_quantity,
    0
  );
  const invalidTransferCount = transferPreviewProducts.filter(
    (item) => item.transfer_quantity > item.sourceStock
  ).length;
  const readyTransferCount = transferPreviewProducts.filter(
    (item) =>
      item.transfer_quantity > 0 && item.transfer_quantity <= item.sourceStock
  ).length;
  const routeLocked =
    sourceLocation &&
    destinationLocation &&
    sourceLocation.id !== destinationLocation.id;

  const loadTransferHistory = async () => {
    setHistoryLoading(true);

    try {
      const result = await dispatch(
        getInventoryTransferHistory({
          sourceLocationId,
          destinationLocationId,
          searchText: historySearch,
          productIds: selectedProducts.map((item) => item.id),
        })
      );

      setHistoryItems(result || []);
    } catch (error) {
      console.error(error);
      message.error("Failed to load transfer history.");
    }

    setHistoryLoading(false);
  };

  useEffect(() => {
    if (!visible || activeTab !== "history") {
      return;
    }

    loadTransferHistory();
  }, [
    activeTab,
    visible,
    sourceLocationId,
    destinationLocationId,
    historySearch,
    selectedProducts,
  ]);

  const handleSubmit = async () => {
    if (!sourceLocation || !destinationLocation) {
      message.warning("Choose both source and destination branches.");
      return;
    }

    if (sourceLocation.id === destinationLocation.id) {
      message.warning("Source and destination branches must be different.");
      return;
    }

    const payloadProducts = buildTransferPayload();

    if (payloadProducts.length === 0) {
      message.warning("Add at least one product with quantity.");
      return;
    }

    const invalidProduct = payloadProducts.find(
      (item) => item.transfer_quantity > item.sourceStock
    );

    if (invalidProduct) {
      message.warning(
        `${invalidProduct.name} only has ${formatStock(
          invalidProduct.sourceStock
        )} in ${sourceLocation.name}.`
      );
      return;
    }

    Modal.confirm({
      title: "Confirm Inventory Transfer",
      content: (
        <div className="inventory-transfer-confirmation">
          <Text>
            Transfer {payloadProducts.length} product
            {payloadProducts.length > 1 ? "s" : ""} from {sourceLocation.name} to{" "}
            {destinationLocation.name}.
          </Text>
          <div className="inventory-transfer-confirmation-list">
            {payloadProducts.map((item) => (
              <div key={item.id}>
                {item.name}: {formatStock(item.transfer_quantity)} {item.unit || ""}
              </div>
            ))}
          </div>
        </div>
      ),
      okText: "Transfer",
      onOk: async () => {
        setSaving(true);

        try {
          const result = await dispatch(
            transferInventoryBetweenLocations({
              products: payloadProducts,
              sourceLocation,
              destinationLocation,
              reason,
              note,
            })
          );

          updateStoredPairLists({
            sourceLocationId: sourceLocation.id,
            sourceLocationName: sourceLocation.name,
            destinationLocationId: destinationLocation.id,
            destinationLocationName: destinationLocation.name,
            updatedAt: new Date().toISOString(),
          });

          message.success(
            `Transferred ${payloadProducts.length} product${
              payloadProducts.length > 1 ? "s" : ""
            } from ${sourceLocation.name} to ${destinationLocation.name}.`
          );

          if (typeof onTransferred === "function") {
            onTransferred(result);
          }

          setActiveTab("history");
          loadTransferHistory();
          setSelectedProducts((currentProducts) =>
            currentProducts.map((item) => ({
              ...item,
              transfer_quantity: 0,
            }))
          );
          setNote("");
        } catch (error) {
          console.error(error);
          message.error(error?.message || "Inventory transfer failed.");
        }

        setSaving(false);
      },
    });
  };

  const renderPairChips = (label, pairs) => {
    if (!pairs.length) {
      return null;
    }

    return (
      <div className="inventory-transfer-pair-group">
        <Text className="inventory-transfer-field-label">{label}</Text>
        <div className="inventory-transfer-pair-list">
          {pairs.map((pair) => (
            <Button
              key={pair.id}
              size="small"
              onClick={() => applyPair(pair)}
              className={
                pair.id === currentPairId ? "inventory-transfer-pair-active" : ""
              }
            >
              {pair.sourceLocationName} to {pair.destinationLocationName}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const renderSelectedProduct = (product) => {
    const sourceStock = getStockForProduct(product, sourceLocationId);
    const destinationStock = getStockForProduct(product, destinationLocationId);
    const transferQuantity = normalizeQty(product.transfer_quantity);
    const sourceAfter = sourceStock - transferQuantity;
    const destinationAfter = destinationStock + transferQuantity;
    const isInvalid = transferQuantity > sourceStock;
    const sourceLabel = sourceLocation?.name || "Source";
    const destinationLabel = destinationLocation?.name || "Destination";
    const transferRatio =
      sourceStock > 0
        ? Math.min((transferQuantity / sourceStock) * 100, 100)
        : 0;

    return (
      <List.Item
        key={product.id}
        className={`inventory-transfer-product ${isInvalid ? "is-invalid" : ""}`}
        actions={[
          <Button
            key="max"
            size="small"
            onClick={() => updateProductQuantity(product.id, sourceStock)}
          >
            Max
          </Button>,
          <Button
            key="remove"
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => removeSelectedProduct(product.id)}
          />,
        ]}
      >
        <div className="inventory-transfer-product-body">
          <div className="inventory-transfer-product-copy">
            <div className="inventory-transfer-product-header">
              <div className="inventory-transfer-summary-product">
                <ImageChecker
                  url={`${process.env.REACT_APP_AWS_S3}${product.id}.png`}
                  success={
                    <img
                      src={`${process.env.REACT_APP_AWS_S3}${product.id}.png`}
                      alt="product"
                      className="inventory-transfer-thumb"
                    />
                  }
                  fail={
                    <img
                      src={`${process.env.REACT_APP_AWS_S3}logo192.png`}
                      alt="product"
                      className="inventory-transfer-thumb"
                    />
                  }
                />
                <Space wrap>
                  <Text strong>{product.name}</Text>
                  {product.sku ? <Tag>{product.sku}</Tag> : null}
                  {product.unit ? <Tag color="blue">{product.unit}</Tag> : null}
                </Space>
              </div>
              <Tag color={isInvalid ? "red" : transferQuantity > 0 ? "green" : "default"}>
                {isInvalid
                  ? "Exceeds Stock"
                  : transferQuantity > 0
                  ? "Ready"
                  : "No Quantity"}
              </Tag>
            </div>

            <Progress
              percent={Math.round(transferRatio)}
              showInfo={false}
              strokeColor={isInvalid ? "#ff4d4f" : "#52c41a"}
              status={isInvalid ? "exception" : "active"}
            />

            <div className="inventory-transfer-stock-grid">
              <Tag color="geekblue">
                {sourceLabel}: {formatStock(sourceStock)}
              </Tag>
              <Tag color="purple">
                {destinationLabel}: {formatStock(destinationStock)}
              </Tag>
              <Tag color={sourceAfter < 0 ? "red" : "cyan"}>
                {sourceLabel} after: {formatStock(sourceAfter)}
              </Tag>
              <Tag color="green">
                {destinationLabel} after: {formatStock(destinationAfter)}
              </Tag>
            </div>
          </div>

          <div className="inventory-transfer-product-qty">
            <Text className="inventory-transfer-field-label">Qty</Text>
            <InputNumber
              min={0}
              value={transferQuantity}
              onChange={(value) => updateProductQuantity(product.id, value)}
              style={{ width: "100%" }}
            />
            {isInvalid ? (
              <Text type="danger">Over by {formatStock(transferQuantity - sourceStock)}.</Text>
            ) : (
              <Text type="secondary">Units to move.</Text>
            )}
          </div>
        </div>
      </List.Item>
    );
  };

  const renderHistoryItem = (item) => {
    return (
      <List.Item key={item.id} className="inventory-transfer-history-item">
        <div className="inventory-transfer-history-copy">
          <Space wrap>
            <Tag icon={<HistoryOutlined />} color="blue">
              {item.created_at}
            </Tag>
            <Tag color="gold">{item.created_name}</Tag>
            <Tag>
              {item.source_location_name} to {item.destination_location_name}
            </Tag>
          </Space>
          {item.reason ? <Text strong>{item.reason}</Text> : null}
          {item.note ? <Text type="secondary">{item.note}</Text> : null}
          <div className="inventory-transfer-history-products">
            {(item.products || []).map((product) => (
              <Tag key={`${item.id}_${product.product || product.id}`}>
                {product.product_name}: {formatStock(product.quantity)}{" "}
                {product.unit || ""}
              </Tag>
            ))}
          </div>
        </div>
      </List.Item>
    );
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <Modal
      visible={visible}
      width={980}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={saving}
          onClick={handleSubmit}
        >
          Transfer Inventory
        </Button>,
      ]}
      className="inventory-transfer-modal"
      destroyOnClose={false}
      title="Transfer Inventory"
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Transfer" key="transfer">
          <div className="inventory-transfer-layout">
            <Alert
              type={routeLocked ? (invalidTransferCount > 0 ? "warning" : "success") : "info"}
              showIcon
              message={
                routeLocked
                  ? `${sourceLocation.name} to ${destinationLocation.name}`
                  : "Choose source and destination branches first"
              }
              description={
                routeLocked
                  ? "Then add products, set quantities, and review stock before submitting."
                  : "The source branch is where stock is taken from. The destination branch receives the stock."
              }
            />

            <Row gutter={[12, 12]} className="inventory-transfer-stat-row">
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Selected Products" value={selectedProducts.length} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Total Quantity"
                    value={totalTransferQuantity}
                    precision={2}
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Ready Lines" value={readyTransferCount} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Warnings"
                    value={invalidTransferCount}
                    valueStyle={invalidTransferCount > 0 ? { color: "#cf1322" } : undefined}
                  />
                </Card>
              </Col>
            </Row>

            <Card
              title="1. Choose Branches"
              extra={
                <Space>
                  <Tooltip title="Swap branches">
                    <Button
                      icon={<RetweetOutlined />}
                      onClick={swapBranches}
                      disabled={!sourceLocationId && !destinationLocationId}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      currentPairIsFavorite
                        ? "Remove favorite pair"
                        : "Save favorite pair"
                    }
                  >
                    <Button
                      icon={currentPairIsFavorite ? <StarFilled /> : <StarOutlined />}
                      onClick={toggleFavoritePair}
                      disabled={!sourceLocationId || !destinationLocationId}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <div className="inventory-transfer-topbar">
                {renderPairChips("Favorites", favoritePairs)}
                {renderPairChips("Recent Pairs", recentPairs)}
              </div>

              <div className="inventory-transfer-branch-grid">
                <div className="inventory-transfer-branch-card">
                  <Text className="inventory-transfer-field-label">Source Branch</Text>
                  <Select
                    showSearch
                    value={sourceLocationId}
                    onChange={setSourceLocationId}
                    placeholder="Select source branch"
                    style={{ width: "100%" }}
                    optionFilterProp="children"
                  >
                    {(locations || []).map((location) => (
                      <Select.Option key={location.id} value={location.id}>
                        {location.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Text type="secondary">
                    {sourceLocation
                      ? `Stock will be deducted from ${sourceLocation.name}.`
                      : "Pick where the products will come from."}
                  </Text>
                </div>

                <div className="inventory-transfer-branch-action">
                  <Tag color={routeLocked ? "green" : "default"}>
                    {routeLocked ? "Route Ready" : "Pending"}
                  </Tag>
                </div>

                <div className="inventory-transfer-branch-card">
                  <Text className="inventory-transfer-field-label">
                    Destination Branch
                  </Text>
                  <Select
                    showSearch
                    value={destinationLocationId}
                    onChange={setDestinationLocationId}
                    placeholder="Select destination branch"
                    style={{ width: "100%" }}
                    optionFilterProp="children"
                  >
                    {(locations || []).map((location) => (
                      <Select.Option key={location.id} value={location.id}>
                        {location.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Text type="secondary">
                    {destinationLocation
                      ? `Stock will be added to ${destinationLocation.name}.`
                      : "Pick where the products will be transferred to."}
                  </Text>
                </div>
              </div>
            </Card>

            <Card title="2. Transfer Details">
              <div className="inventory-transfer-meta-grid">
                <div>
                  <Text className="inventory-transfer-field-label">Reason</Text>
                  <Select
                    value={reason}
                    onChange={setReason}
                    style={{ width: "100%" }}
                  >
                    {REASON_OPTIONS.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Text className="inventory-transfer-field-label">Notes</Text>
                  <TextArea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    rows={2}
                    placeholder="Optional transfer notes"
                  />
                </div>
              </div>
            </Card>

            <Card
              className="inventory-transfer-products-card"
              title="3. Add Products"
              extra={
                <Space>
                  <Button
                    icon={<SearchOutlined />}
                    type={showOnlyAvailable ? "primary" : "default"}
                    ghost={showOnlyAvailable}
                    onClick={() => setShowOnlyAvailable((current) => !current)}
                  >
                    {showOnlyAvailable ? "In-Stock Only" : "Show All"}
                  </Button>
                </Space>
              }
            >
              <div className="inventory-transfer-selector-copy">
                <Text type="secondary">
                  Search by product name or SKU, then type the transfer quantity directly in the row.
                </Text>
              </div>

              <div className="inventory-transfer-filter-row">
                <Input
                  allowClear
                  placeholder="Search products by name or SKU"
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.target.value)}
                />
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Filter by category"
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  options={categoryOptions}
                  maxTagCount="responsive"
                />
              </div>

              <Table
                rowKey="id"
                className="inventory-transfer-products-table"
                columns={productTableColumns}
                dataSource={productCatalog}
                size="small"
                pagination={{
                  pageSize: 8,
                  hideOnSinglePage: true,
                  responsive: true,
                  showSizeChanger: false,
                  size: "small",
                }}
                scroll={{ x: 720 }}
              />

              {selectedProducts.length > 0 ? (
                <>
                  <div className="inventory-transfer-selector-copy">
                    <Text strong>Transfer Summary</Text>
                    <br />
                    <Text type="secondary">
                      Review only the products with transfer quantity before submitting.
                    </Text>
                  </div>
                  <List
                    className="inventory-transfer-products-list"
                    dataSource={selectedProducts}
                    renderItem={renderSelectedProduct}
                  />
                </>
              ) : (
                <div className="inventory-transfer-empty-state">
                  <Empty
                    description="No transfer quantity added yet."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </Card>
          </div>
        </TabPane>

        <TabPane tab="History" key="history">
          <Card title="Transfer History">
            <div className="inventory-transfer-history-toolbar">
              <Input
                value={historySearch}
                onChange={(event) => setHistorySearch(event.target.value)}
                placeholder="Search by product, SKU, branch, reason, or note"
                allowClear
              />
              <Button onClick={loadTransferHistory}>Refresh</Button>
            </div>

            {historyLoading ? (
              <div className="inventory-transfer-history-loading">
                <Spin />
              </div>
            ) : historyItems.length > 0 ? (
              <List
                dataSource={historyItems}
                renderItem={renderHistoryItem}
                className="inventory-transfer-history-list"
              />
            ) : (
              <div className="inventory-transfer-empty-state">
                <Empty
                  description="No transfer history found for the current filters."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default Index;
